import React, { useState, useEffect } from "react";
import {
  Shield, Plus, Edit, Trash2, ArrowUpRight,
  DollarSign, Settings, Filter, Search, Star,
  AlertCircle, X, Award, Check, Building2,
  Calculator, ChevronDown, LockIcon, UnlockIcon,
  Clock, FileText, PercentIcon, RefreshCcw
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import DepositDialog from "../../components/ui/DepositDialog";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
  Deposit,
  DepositBackendResponse,
  DepositStatusEnum,
  DepositCategoryTypeEnum,
  DepositRuleTypeEnum
} from "../../modules/shared/model";
import { 
  fetchDeposits, 
  deleteDeposit, 
  clearDepositError 
} from "../../redux/Slices/depositSlice";

const DepositRules = () => {
  const dispatch = useAppDispatch();
  const { items: depositRules = [], loading, error, totalCount } = useAppSelector((s: any) => s.deposits);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<DepositBackendResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Load deposits on component mount
  useEffect(() => {
    dispatch(fetchDeposits(undefined));
  }, [dispatch]);

  // Clear error when component unmounts or error is shown
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearDepositError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Filter deposits based on search
  const filteredDeposits = depositRules.filter(rule => {
    if (!rule) return false;
    
    // Search filtering
    const matchesSearch = !searchQuery || rule.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  // Calculate stats from actual data
  const stats = {
    activeRules: depositRules.filter(rule => rule?.status?.id === "1").length, // Backend uses string IDs
    avgDeposit: depositRules.length > 0 
      ? Math.round(depositRules.reduce((sum, rule) => sum + (rule?.holdingPeriod || 0), 0) / depositRules.length * 10) / 10
      : 0,
    holdTime: depositRules.length > 0 
      ? Math.round(depositRules.reduce((sum, rule) => sum + (rule?.holdingPeriod || 0), 0) / depositRules.length)
      : 0,
    successRate: 98 // This would come from backend stats
  };

  const handleDeleteDeposit = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this deposit rule?")) {
      try {
        await dispatch(deleteDeposit(id)).unwrap();
        // Fetch latest deposits from backend to show updated data
        await dispatch(fetchDeposits(undefined)).unwrap();
      } catch (error) {
        console.error("Error deleting deposit:", error);
      }
    }
  };

  const getEnumDisplayName = (enumType: any, value: number): string => {
    const enumKey = Object.keys(enumType).find(key => enumType[key] === value);
    return enumKey ? enumKey.replace(/_/g, " ") : "";
  };


  const DepositRuleCard = ({ rule }: { rule: DepositBackendResponse }) => {
    if (!rule) {
      return null;
    }

    return (
      <Card className="p-6"   style={{ textAlign: "left" }}>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{rule.name || "Unnamed Deposit"}</h3>
              <Badge variant={undefined} className={
                rule.status?.id === "1" ? "bg-green-100 text-green-700" :
                "bg-yellow-100 text-yellow-700"
              }>
                {rule.status?.name || (rule.status?.id === "1" ? "Active" : "Inactive")}
              </Badge>
            </div>
            {rule.user && (
              <p className="text-gray-500 text-sm mt-1">
                User: {rule.user.firstName} {rule.user.lastName} ({rule.user.email})
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedRule(rule)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600"
              onClick={() => handleDeleteDeposit(rule.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Deposit Rules */}
        <div className="mt-6"   style={{ textAlign: "left" }}>
          <h4 className="font-medium mb-4">Deposit Requirements</h4>
          <div className="space-y-4">
            {rule.depositRules && rule.depositRules.length > 0 ? rule.depositRules.map((depositRule, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={undefined} className={
                      depositRule.type?.id === "1" ? "bg-blue-100 text-blue-700" :
                      "bg-purple-100 text-purple-700"
                    }>
                      {depositRule.type?.name || "Unknown Type"}
                    </Badge>
                    <span className="font-medium">{depositRule.amount}</span>
                  </div>
                  <Badge variant="outline">
                    {depositRule.threshold}
                  </Badge>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCcw className="h-4 w-4 text-gray-400" />
                    {depositRule.refundPolicy}
                  </div>
                  {depositRule.conditions && depositRule.conditions.map((condition, condIndex) => (
                    <div key={condIndex} className="flex items-center gap-2 ml-1 mt-1">
                      <Check className="h-4 w-4 text-green-600" />
                      {condition}
                    </div>
                  ))}
                </div>
              </div>
            )) : (
              <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                No deposit rules configured
              </div>
            )}
          </div>
        </div>

        {/* Release Conditions */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">Release Conditions</h4>
            <Badge variant="outline">Holding Period: {rule.holdingPeriod}h</Badge>
          </div>
          <div className="space-y-2">
            {rule.releaseConditions && rule.releaseConditions.length > 0 ? rule.releaseConditions.map((condition, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-600" />
                {condition}
              </div>
            )) : (
              <div className="text-sm text-gray-500">No release conditions configured</div>
            )}
          </div>
        </div>

        {/* Exceptions */}
        <div className="mt-6">
          <h4 className="font-medium mb-4">Special Exceptions</h4>
          <div className="grid grid-cols-2 gap-4">
            {rule.depositExceptions && rule.depositExceptions.length > 0 ? rule.depositExceptions.map((exception, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant={undefined} className="bg-yellow-100 text-yellow-700">
                    {exception.type}
                  </Badge>
                </div>
                <p className="text-sm font-medium mb-2">{exception.policy}</p>
                <div className="space-y-1">
                  {exception.conditions && exception.conditions.map((condition, condIndex) => (
                    <div key={condIndex} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-600" />
                      {condition}
                    </div>
                  ))}
                </div>
              </div>
            )) : (
              <div className="col-span-2 p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                No exceptions configured
              </div>
            )}
          </div>
        </div>

        {/* Settings */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-medium mb-4">Rule Settings</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">Auto Release</div>
              <Badge variant={undefined} className={rule.autoRelease ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                {rule.autoRelease ? "Enabled" : "Disabled"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">Dispute Window</div>
              <Badge variant="outline">{rule.disputeWindow}h</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">Instant Release</div>
              <Badge variant={undefined} className={rule.instantRelease ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                {rule.instantRelease ? "Enabled" : "Disabled"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">Approval Required</div>
              <Badge variant={undefined} className={rule.requireApproval ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}>
                {rule.requireApproval ? "Yes" : "No"}
              </Badge>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-left">
                <h1 className="text-2xl font-bold text-gray-900">Deposit Rules</h1>
                <p className="text-gray-600 mt-1">Manage service deposit requirements and policies</p>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Rule
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Rules</p>
                    <p className="text-2xl font-semibold mt-1">{stats.activeRules}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Avg Hold Time</p>
                    <p className="text-2xl font-semibold mt-1">{stats.holdTime}h</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Rules</p>
                    <p className="text-2xl font-semibold mt-1">{depositRules.length}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <PercentIcon className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Success Rate</p>
                    <p className="text-2xl font-semibold mt-1">{stats.successRate}%</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Check className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search deposit rules..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                
              </div>
            </Card>

            {/* Deposit Rules */}
            <div className="grid grid-cols-1 gap-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading deposit rules...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <div className="text-red-600 mb-2">Error loading deposit rules</div>
                  <p className="text-gray-600">{error}</p>
                </div>
              ) : filteredDeposits.length > 0 ? (
                filteredDeposits.map(rule => (
                  <DepositRuleCard key={rule.id} rule={rule} />
                ))
              ) : (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No deposit rules found</h3>
                  <p className="text-gray-600 mb-4">
                    {searchQuery 
                      ? "No deposit rules match your current search." 
                      : "Get started by creating your first deposit rule."}
                  </p>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Deposit Rule
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Deposit Dialog */}
      <DepositDialog
        isOpen={showAddModal || !!selectedRule}
        onClose={async () => {
          setShowAddModal(false);
          setSelectedRule(null);
          // Refresh data when dialog closes to ensure we have the latest data
          try {
            await dispatch(fetchDeposits(undefined));
          } catch (error) {
            console.error("Error refreshing deposits:", error);
          }
        }}
        deposit={selectedRule}
      />
    </div>
  );
};

export default DepositRules;